import React from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ArtAlert, ArtAsk, ArtCollect, ArtSignup, Logo } from '../src/brand';
import { KakaoButton } from '../src/KakaoButton';
import { markWelcomeSeen } from '../src/welcome';
import { D } from '../src/theme';

/**
 * 첫 실행 화면.
 *
 * 로그인을 강제하면 딜 앱은 둘러보러 온 사람이 그대로 나간다. 그래서 가입은 크게 권하되
 * 안 하고도 들어갈 수 있게 하고, 그 길로 들어오는 사람에게는 이 앱이 뭘 하는지부터 보여 준다.
 * 설명 없이 피드만 던지면 다른 딜 앱과 구분이 안 된다.
 */
type Step = { Art: React.ComponentType<{ size?: number }>; title: string; body: string };

const STEPS: Step[] = [
  {
    Art: ArtCollect,
    title: '흩어진 특가를 한곳에',
    body: '커뮤니티에 올라오는 특가를 5분마다 모아서 시장가·반응·판매처를 따져 점수를 매겨요. 70점 넘는 것만 올립니다.',
  },
  {
    Art: ArtAsk,
    title: '상품 이름을 몰라도 돼요',
    body: '"알약이랑 물약 같이 먹는 영양제" 처럼 적으면 AI 가 되물어서 같이 좁혀요. 그런 제품을 파는 회사를 전부 찾아 등록해요.',
  },
  {
    Art: ArtAlert,
    title: '싸게 뜨면 그때 알려드려요',
    body: '등록해 둔 조건에 맞는 딜이 올라오면 알림이 가요. 엉뚱한 상품이 아닌지 보내기 전에 AI 가 한 번 더 확인합니다.',
  },
];

export default function Welcome() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const [tutorial, setTutorial] = React.useState(false);

  async function enter() {
    await markWelcomeSeen();
    router.replace('/(tabs)');
  }

  if (tutorial) {
    return (
      <View style={{ flex: 1, backgroundColor: D.ground }}>
        <ScrollView contentContainerStyle={{ paddingHorizontal: 16, paddingTop: insets.top + 24, paddingBottom: 20, gap: 14 }}>
          <Text style={s.h1}>이렇게 쓰는 앱이에요</Text>
          {STEPS.map((st) => (
            <View key={st.title} style={s.step}>
              <st.Art size={84} />
              <View style={{ gap: 5 }}>
                <Text style={s.stepTitle}>{st.title}</Text>
                <Text style={s.stepBody}>{st.body}</Text>
              </View>
            </View>
          ))}

          <View style={s.signup}>
            <ArtSignup size={104} />
            <View style={{ flex: 1, minWidth: 0, gap: 4 }}>
              <Text style={s.signupTitle}>가입하면 달라지는 것</Text>
              <Text style={s.signupBody}>
                가입하면 크레딧 20개, 매일 5개씩 더. 긴 문장도 되고 AI 가 되물어 가며
                같이 찾아 줘요. 물론 전부 무료입니다.
              </Text>
            </View>
          </View>
        </ScrollView>
        <View style={[s.dock, { paddingBottom: insets.bottom + 16 }]}>
          <Pressable onPress={enter} style={({ pressed }) => [s.primary, pressed && { opacity: 0.85 }]}>
            <Text style={s.primaryText}>둘러보기 시작</Text>
          </Pressable>
          <Pressable onPress={() => setTutorial(false)} hitSlop={8} style={{ alignSelf: 'center', paddingVertical: 10 }}>
            <Text style={s.quiet}>뒤로</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  return (
    <View style={{ flex: 1, backgroundColor: D.ground }}>
      <View style={[s.hero, { paddingTop: insets.top }]}>
        <Logo size={58} tone={D.ink} />
        <Text style={s.title}>필요한 물건이{'\n'}싸게 뜨면 알려드려요</Text>
        <Text style={s.lead}>
          커뮤니티 특가를 모아 점수로 거르고 원하는 조건은 AI 가 같이 좁혀 줘요.
        </Text>
      </View>

      <View style={[s.dock, { paddingBottom: insets.bottom + 16 }]}>
        <KakaoButton onDone={enter} note="전부 무료예요. 결제 화면은 아예 없습니다." />
        <Pressable onPress={() => setTutorial(true)} hitSlop={10} style={{ alignSelf: 'center', paddingVertical: 12 }}>
          <Text style={s.skip}>회원가입 없이 시작하기</Text>
        </Pressable>
      </View>
    </View>
  );
}

const s = StyleSheet.create({
  hero: { flex: 1, justifyContent: 'center', paddingHorizontal: 16, gap: 14, paddingBottom: 40 },
  title: { fontSize: 29, fontWeight: '900', lineHeight: 39, color: D.ink, letterSpacing: -1.2 },
  lead: { fontSize: 14.5, lineHeight: 22, color: D.ink2, fontWeight: '600' },

  dock: { paddingHorizontal: 16, paddingTop: 12, gap: 2 },
  skip: { fontSize: 13, fontWeight: '800', color: D.ink, textDecorationLine: 'underline' },
  quiet: { fontSize: 13, fontWeight: '800', color: D.ink2 },
  primary: { backgroundColor: D.ink, paddingVertical: 14, alignItems: 'center' },
  primaryText: { fontSize: 15, fontWeight: '900', color: D.volt, letterSpacing: -0.2 },

  h1: { fontSize: 23, fontWeight: '900', color: D.ink, letterSpacing: -0.7, marginBottom: 2 },
  step: { alignItems: 'center', gap: 10, backgroundColor: D.panel, borderWidth: 1, borderColor: D.ink, paddingVertical: 20, paddingHorizontal: 18 },
  stepTitle: { fontSize: 16, fontWeight: '800', color: D.ink, textAlign: 'center', letterSpacing: -0.3 },
  stepBody: { fontSize: 13, lineHeight: 20, color: D.ink2, textAlign: 'center' },

  // 가입 안내만 형광으로 띄운다 — 이 화면에서 눌러 주길 바라는 것이 그것뿐이라서
  signup: { flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: D.volt, borderWidth: 1, borderColor: D.ink, padding: 14, marginTop: 2 },
  signupTitle: { fontSize: 14, fontWeight: '900', color: D.ink, letterSpacing: -0.2 },
  signupBody: { fontSize: 12.5, lineHeight: 19, color: D.ink, fontWeight: '600' },
});
